import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchWatchlistsApi,
  createWatchlistApi,
  deleteWatchlistApi,
  addAktieApi,
  removeAktieApi,
} from './aktienApi';

/**
 * Redux-Slice fuer Watchlists (selbst angelegte Aktien-Listen).
 *
 * @typedef {import('types/dto').WatchlistDTO} WatchlistDTO
 */

export const fetchWatchlists = createAsyncThunk('watchlists/fetchAll', async () => {
  return (await fetchWatchlistsApi()) ?? [];
});

export const createWatchlist = createAsyncThunk('watchlists/create', async (name) => {
  return await createWatchlistApi(name);
});

export const deleteWatchlist = createAsyncThunk('watchlists/delete', async (uuid) => {
  await deleteWatchlistApi(uuid);
  return uuid;
});

export const addAktieToWatchlist = createAsyncThunk(
  'watchlists/addAktie',
  async ({ uuid, aktie }) => {
    return await addAktieApi(uuid, aktie);
  }
);

export const removeAktieFromWatchlist = createAsyncThunk(
  'watchlists/removeAktie',
  async ({ uuid, isin }) => {
    return await removeAktieApi(uuid, isin);
  }
);

const initialState = {
  items: [],
  status: 'idle', // idle | loading | succeeded | failed
  error: null,
};

/** Ersetzt eine Watchlist (per uuid) im State durch die aktualisierte Version. */
const upsert = (state, watchlist) => {
  if (!watchlist) return;
  const idx = state.items.findIndex((w) => w.uuid === watchlist.uuid);
  if (idx >= 0) {
    state.items[idx] = watchlist;
  } else {
    state.items.push(watchlist);
  }
};

const watchlistsSlice = createSlice({
  name: 'watchlists',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWatchlists.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchWatchlists.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchWatchlists.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createWatchlist.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(deleteWatchlist.fulfilled, (state, action) => {
        state.items = state.items.filter((w) => w.uuid !== action.payload);
      })
      .addCase(addAktieToWatchlist.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(removeAktieFromWatchlist.fulfilled, (state, action) => {
        upsert(state, action.payload);
      });
  },
});

export const selectAllWatchlists = (state) => state.watchlists.items;
export const selectWatchlistsStatus = (state) => state.watchlists.status;
export const selectWatchlistsError = (state) => state.watchlists.error;

export default watchlistsSlice.reducer;
